import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/StatusBadge';
import { ClipboardCheck, CheckCircle, Play, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ConteoFisico() {
  const [tab, setTab] = useState('nuevo');
  const [warehouses, setWarehouses] = useState([]);
  const [conteos, setConteos] = useState([]);
  const [saving, setSaving] = useState(false);

  // New count form
  const [warehouseId, setWarehouseId] = useState('');
  const [responsable, setResponsable] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);

  useEffect(() => {
    Promise.all([API.get('/catalogs/warehouses'), API.get('/conteo')])
      .then(([w, c]) => { setWarehouses(w.data); setConteos(c.data); });
  }, []);

  // Load system balances when warehouse selected
  useEffect(() => {
    if (warehouseId) {
      setLoadingBalances(true);
      API.get(`/conteo/sistema/${warehouseId}`).then(res => {
        setLineas(res.data.map(b => ({
          group_code: b.group_code,
          existencia_sistema: b.existencia_sistema,
          conteo_fisico: '',
          diferencia: null
        })));
      }).finally(() => setLoadingBalances(false));
    }
  }, [warehouseId]);

  const updateLinea = (idx, value) => {
    const updated = [...lineas];
    const conteo = parseInt(value) || 0;
    updated[idx].conteo_fisico = value;
    updated[idx].diferencia = value !== '' ? conteo - updated[idx].existencia_sistema : null;
    setLineas(updated);
  };

  const totalDiferencias = lineas.filter(l => l.diferencia !== null && l.diferencia !== 0).length;

  const handleCreate = async () => {
    if (!warehouseId || !responsable) { toast.error('Selecciona almacén y responsable'); return; }
    const validLines = lineas.filter(l => l.conteo_fisico !== '');
    if (validLines.length === 0) { toast.error('Ingresa al menos un conteo'); return; }
    setSaving(true);
    try {
      const res = await API.post('/conteo', {
        warehouse_id: warehouseId, responsable, observaciones,
        lineas: validLines.map(l => ({ group_code: l.group_code, conteo_fisico: parseInt(l.conteo_fisico) }))
      });
      toast.success(`Conteo creado: ${res.data.folio}`);
      setWarehouseId(''); setResponsable(''); setObservaciones(''); setLineas([]);
      const c = await API.get('/conteo'); setConteos(c.data);
      setTab('historial');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleAutorizar = async (folio) => {
    if (!window.confirm(`¿Autorizar conteo ${folio}?`)) return;
    try {
      await API.post(`/conteo/${folio}/autorizar`);
      toast.success('Conteo autorizado');
      const c = await API.get('/conteo'); setConteos(c.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleAplicar = async (folio) => {
    if (!window.confirm(`¿Aplicar ajustes del conteo ${folio}? Esto modificará el inventario.`)) return;
    try {
      const res = await API.post(`/conteo/${folio}/aplicar`);
      toast.success(`Ajustes aplicados: ${res.data.ajustes?.length || 0} grupos modificados`);
      const c = await API.get('/conteo'); setConteos(c.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const STATUS_MAP = {
    borrador: { label: 'Borrador', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    autorizado: { label: 'Autorizado', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    aplicado: { label: 'Aplicado', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Conteo Físico</h1>
        <p className="text-sm text-muted-foreground">Comparar inventario del sistema vs conteo real y aplicar ajustes</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="nuevo">Nuevo Conteo</TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="nuevo" className="mt-4 space-y-4">
          <Card className="bg-white border">
            <CardHeader className="pb-3"><CardTitle className="text-base">Datos del Conteo</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">Almacén *</label>
                  <Select value={warehouseId} onValueChange={setWarehouseId}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Responsable *</label>
                  <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} placeholder="Quién realizó el conteo" className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Observaciones</label>
                  <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Opcional" className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {loadingBalances && <p className="text-sm text-muted-foreground">Cargando existencias del sistema...</p>}

          {lineas.length > 0 && (
            <Card className="bg-white border">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Conteo por Grupo</CardTitle>
                {totalDiferencias > 0 && (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> {totalDiferencias} diferencia(s)
                  </span>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-muted/30">
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Sistema</TableHead>
                    <TableHead>Conteo Real</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {lineas.map((l, idx) => (
                      <TableRow key={l.group_code} className={l.diferencia !== null && l.diferencia !== 0 ? 'bg-amber-50/50' : ''}>
                        <TableCell className="font-medium">{l.group_code}</TableCell>
                        <TableCell className="text-right font-semibold">{l.existencia_sistema}</TableCell>
                        <TableCell>
                          <Input type="number" min="0" value={l.conteo_fisico}
                            onChange={(e) => updateLinea(idx, e.target.value)}
                            placeholder="Contar..." className="w-28" />
                        </TableCell>
                        <TableCell className="text-right">
                          {l.diferencia !== null && (
                            <span className={`font-semibold ${l.diferencia > 0 ? 'text-emerald-600' : l.diferencia < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {l.diferencia > 0 ? '+' : ''}{l.diferencia}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {lineas.length > 0 && (
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
                <ClipboardCheck className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : 'Registrar Conteo'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Almacén</TableHead><TableHead>Responsable</TableHead>
                <TableHead>Diferencias</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {conteos.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin conteos registrados</TableCell></TableRow>}
                {conteos.map(c => (
                  <TableRow key={c.folio} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{c.folio}</TableCell>
                    <TableCell>{c.warehouse_nombre}</TableCell>
                    <TableCell>{c.responsable}</TableCell>
                    <TableCell>
                      {c.tiene_diferencias ? (
                        <div className="flex flex-wrap gap-1">
                          {c.lineas?.filter(l => l.diferencia !== 0).map(l => (
                            <span key={l.group_code} className={`text-[10px] px-1.5 py-0.5 rounded ${l.diferencia > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {l.group_code}: {l.diferencia > 0 ? '+' : ''}{l.diferencia}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-xs text-emerald-600">Sin diferencias</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_MAP[c.estatus]?.className || 'bg-gray-100'}`}>
                        {STATUS_MAP[c.estatus]?.label || c.estatus}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(c.fecha).toLocaleDateString('es-MX')}</TableCell>
                    <TableCell className="text-right">
                      {c.estatus === 'borrador' && (
                        <Button size="sm" variant="outline" onClick={() => handleAutorizar(c.folio)}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" /> Autorizar
                        </Button>
                      )}
                      {c.estatus === 'autorizado' && (
                        <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => handleAplicar(c.folio)}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Aplicar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
